import React, { useMemo } from 'react';
import { Invoice } from '../../config';
import { formatCurrency, formatCurrencyCompact } from '../../../../utils/currency';
import StatusPill from '../../../../components/StatusPill';
import { formatDbDate } from './dates';

interface InvoicesTabProps {
  invoices: Invoice[];
  onOpenInvoice: (invoiceId: number) => void;
}

type PillTone = React.ComponentProps<typeof StatusPill>['tone'];

// The invoice lifecycle: a Draft is ours alone, Sent is with the client,
// Entered is in the accounts. Anything unrecognised stays neutral.
const INVOICE_TONE: Record<string, PillTone> = {
  Draft: 'neutral',
  Sent: 'info',
  Entered: 'success',
};

const InvoicesTab: React.FC<InvoicesTabProps> = ({ invoices, onOpenInvoice }) => {
  const totals = useMemo(() => {
    let fee = 0;
    let disbursements = 0;
    let total = 0;
    let draftFee = 0;
    let draftCount = 0;
    for (const inv of invoices) {
      fee += Number(inv.our_fee) || 0;
      disbursements += Number(inv.disbursements) || 0;
      total += Number(inv.total_amount) || 0;
      if (inv.status === 'Draft') {
        draftFee += Number(inv.our_fee) || 0;
        draftCount += 1;
      }
    }
    return { fee, disbursements, total, draftFee, draftCount };
  }, [invoices]);

  if (invoices.length === 0) {
    return (
      <div className="job-card-tab">
        <div className="ui-table__empty">No invoices have been raised for this job yet.</div>
      </div>
    );
  }

  return (
    <div className="job-card-tab">
      <div className="stat-grid">
        <div className="stat">
          <span className="stat__value">{invoices.length}</span>
          <span className="stat__label">Invoices</span>
        </div>
        <div className="stat">
          <span className="stat__value">{formatCurrencyCompact(totals.fee)}</span>
          <span className="stat__label">Total invoiced, ex GST</span>
        </div>
        <div className="stat">
          <span className="stat__value">{formatCurrencyCompact(totals.disbursements)}</span>
          <span className="stat__label">Disbursements</span>
        </div>
        <div className="stat">
          <span className="stat__value">
            {totals.draftCount > 0 ? formatCurrencyCompact(totals.draftFee) : '—'}
          </span>
          <span className="stat__label">
            {totals.draftCount > 0 ? `In draft (${totals.draftCount})` : 'In draft'}
          </span>
        </div>
      </div>

      <table className="ui-table">
        <thead>
          <tr>
            <th className="col-date">Date</th>
            <th className="col-code">Invoice #</th>
            <th>Status</th>
            <th className="is-num">Total fee invoiced</th>
            <th className="is-num">Disbursements</th>
            <th className="is-num">Total inc GST</th>
          </tr>
        </thead>
        <tbody>
          {invoices.map((inv) => (
            <tr
              key={inv.id}
              className="is-clickable"
              onClick={() => onOpenInvoice(inv.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onOpenInvoice(inv.id);
                }
              }}
              title={`Open ${inv.invoice_number} in the invoice builder`}
            >
              <td>{formatDbDate(inv.invoice_date)}</td>
              <td className="ui-table__mono">{inv.invoice_number}</td>
              <td>
                {inv.status ? (
                  <StatusPill label={inv.status} tone={INVOICE_TONE[inv.status] ?? 'neutral'} />
                ) : (
                  <span className="ui-table__na">—</span>
                )}
              </td>
              <td className="is-num">{formatCurrency(Number(inv.our_fee) || 0)}</td>
              <td className="is-num">{formatCurrency(Number(inv.disbursements) || 0)}</td>
              <td className="is-num">{formatCurrency(Number(inv.total_amount) || 0)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot className="ui-table__totals">
          <tr>
            <td colSpan={3}>Total</td>
            <td className="is-num">{formatCurrency(totals.fee)}</td>
            <td className="is-num">{formatCurrency(totals.disbursements)}</td>
            <td className="is-num">{formatCurrency(totals.total)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

export default InvoicesTab;
